// Single source of truth for state availability.
// Edit `eligible: false` to add states where Olive hasn't launched yet.
// `metros` powers the local-relevance copy on programmatic /states/:slug pages.
// `pharmacy` flags states that host one of Olive's 503A compounding partners.
export type State = {
  code: string;
  name: string;
  slug: string;
  eligible: boolean;
  metros: string[];
  pharmacy: boolean;
};

const RAW: Array<[string, string, boolean, string[], boolean?]> = [
  ["AL", "Alabama", true, ["Birmingham", "Huntsville", "Mobile"]],
  ["AK", "Alaska", true, ["Anchorage", "Fairbanks", "Juneau"]],
  ["AZ", "Arizona", true, ["Phoenix", "Tucson", "Scottsdale"]],
  ["AR", "Arkansas", true, ["Little Rock", "Fayetteville", "Bentonville"]],
  ["CA", "California", true, ["Los Angeles", "San Francisco", "San Diego", "Sacramento"]],
  ["CO", "Colorado", true, ["Denver", "Boulder", "Colorado Springs"]],
  ["CT", "Connecticut", true, ["Hartford", "New Haven", "Stamford"]],
  ["DE", "Delaware", true, ["Wilmington", "Dover", "Newark"]],
  ["FL", "Florida", true, ["Miami", "Orlando", "Tampa", "Jacksonville"], true],
  ["GA", "Georgia", true, ["Atlanta", "Savannah", "Augusta"]],
  ["HI", "Hawaii", true, ["Honolulu", "Hilo", "Kailua"]],
  ["ID", "Idaho", true, ["Boise", "Meridian", "Coeur d'Alene"]],
  ["IL", "Illinois", true, ["Chicago", "Naperville", "Springfield"]],
  ["IN", "Indiana", true, ["Indianapolis", "Fort Wayne", "Bloomington"]],
  ["IA", "Iowa", true, ["Des Moines", "Cedar Rapids", "Iowa City"]],
  ["KS", "Kansas", true, ["Wichita", "Overland Park", "Kansas City"]],
  ["KY", "Kentucky", true, ["Louisville", "Lexington", "Bowling Green"]],
  ["LA", "Louisiana", true, ["New Orleans", "Baton Rouge", "Lafayette"]],
  ["ME", "Maine", true, ["Portland", "Bangor", "Augusta"]],
  ["MD", "Maryland", true, ["Baltimore", "Bethesda", "Annapolis"]],
  ["MA", "Massachusetts", true, ["Boston", "Cambridge", "Worcester"]],
  ["MI", "Michigan", true, ["Detroit", "Ann Arbor", "Grand Rapids"]],
  ["MN", "Minnesota", true, ["Minneapolis", "Saint Paul", "Rochester"]],
  ["MS", "Mississippi", true, ["Jackson", "Gulfport", "Oxford"]],
  ["MO", "Missouri", true, ["St. Louis", "Kansas City", "Springfield"]],
  ["MT", "Montana", true, ["Bozeman", "Billings", "Missoula"]],
  ["NE", "Nebraska", true, ["Omaha", "Lincoln", "Bellevue"]],
  ["NV", "Nevada", true, ["Las Vegas", "Reno", "Henderson"]],
  ["NH", "New Hampshire", true, ["Manchester", "Nashua", "Portsmouth"]],
  ["NJ", "New Jersey", true, ["Newark", "Jersey City", "Princeton"]],
  ["NM", "New Mexico", true, ["Albuquerque", "Santa Fe", "Las Cruces"]],
  ["NY", "New York", true, ["New York", "Brooklyn", "Buffalo", "Rochester"]],
  ["NC", "North Carolina", true, ["Charlotte", "Raleigh", "Asheville"]],
  ["ND", "North Dakota", true, ["Fargo", "Bismarck", "Grand Forks"]],
  ["OH", "Ohio", true, ["Columbus", "Cleveland", "Cincinnati"]],
  ["OK", "Oklahoma", true, ["Oklahoma City", "Tulsa", "Norman"]],
  ["OR", "Oregon", true, ["Portland", "Eugene", "Bend"]],
  ["PA", "Pennsylvania", true, ["Philadelphia", "Pittsburgh", "Harrisburg"]],
  ["RI", "Rhode Island", true, ["Providence", "Newport", "Warwick"]],
  ["SC", "South Carolina", true, ["Charleston", "Columbia", "Greenville"]],
  ["SD", "South Dakota", true, ["Sioux Falls", "Rapid City", "Aberdeen"]],
  ["TN", "Tennessee", true, ["Nashville", "Memphis", "Knoxville"]],
  ["TX", "Texas", true, ["Austin", "Dallas", "Houston", "San Antonio"], true],
  ["UT", "Utah", true, ["Salt Lake City", "Park City", "Provo"]],
  ["VT", "Vermont", true, ["Burlington", "Montpelier", "Stowe"]],
  ["VA", "Virginia", true, ["Richmond", "Arlington", "Virginia Beach"]],
  ["WA", "Washington", true, ["Seattle", "Bellevue", "Spokane"]],
  ["WV", "West Virginia", true, ["Charleston", "Morgantown", "Huntington"]],
  ["WI", "Wisconsin", true, ["Milwaukee", "Madison", "Green Bay"]],
  ["WY", "Wyoming", true, ["Cheyenne", "Jackson", "Casper"]],
  ["PR", "Puerto Rico", true, ["San Juan", "Bayamón", "Ponce"]],
];

export const STATES: State[] = RAW.map(([code, name, eligible, metros, pharmacy]) => ({
  code,
  name,
  slug: name.toLowerCase().replace(/\s+/g, "-"),
  eligible,
  metros,
  pharmacy: !!pharmacy,
}));

export const ELIGIBLE_COUNT = STATES.filter((s) => s.eligible).length;
export const STATE_BY_SLUG = Object.fromEntries(STATES.map((s) => [s.slug, s]));
export const STATE_BY_CODE = Object.fromEntries(STATES.map((s) => [s.code, s]));
